import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteDialog";
import { IMAGES, waLink } from "@/data/site";
import {
  Container, QuickQuote, TrustStrip, ServicesSection, WhySection, HowSection,
  SplitFeature, BusinessSection, ReviewsSection, GallerySection, CoverageMap, DarkCTA,
} from "@/components/Sections";
import { MOVING_ITEMS } from "@/data/site";

const Hero = () => {
  const { openQuote } = useQuote();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroFleet} alt="Flotta AXAL Logistics" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />

      <Container className="relative pt-28 pb-40 md:pb-48">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF5A00] mb-5">
          AXAL Logistics — Trasporti · Spedizioni · Traslochi
        </motion.p>
        <h1 className="font-head text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight text-white leading-[1.02]">
          <MaskLine delay={0.15}>Trasportiamo</MaskLine>
          <MaskLine delay={0.28}>ciò che conta.</MaskLine>
          <MaskLine delay={0.41}><span className="text-[#FF5A00]">In tutta Italia.</span></MaskLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="text-white/80 text-lg md:text-xl mt-7 max-w-xl leading-relaxed">
          Trasporti, spedizioni e traslochi professionali per privati e aziende.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
          className="flex flex-col sm:flex-row gap-4 mt-9">
          <button onClick={() => openQuote()} data-testid="hero-quote-button"
            className="h-14 px-8 rounded-full bg-[#FF5A00] text-white font-bold text-lg hover:bg-[#E04F00] transition-colors hover:-translate-y-0.5 duration-200 shadow-[0_0_40px_rgba(255,90,0,0.35)]">
            Richiedi un preventivo
          </button>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="hero-consultant-button"
            className="h-14 px-8 rounded-full border border-white/30 backdrop-blur-sm text-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-white/10 transition-colors">
            Parla con un consulente <ArrowRight size={18} />
          </a>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-12 text-white/85 text-sm font-medium">
          <span className="flex items-center gap-2"><Star size={16} className="text-[#FF5A00] fill-[#FF5A00]" /> 4,9/5 Google</span>
          <span className="w-1 h-1 rounded-full bg-white/40 hidden sm:block" />
          <span>200+ recensioni</span>
          <span className="w-1 h-1 rounded-full bg-white/40 hidden sm:block" />
          <span>Trasporti nazionali</span>
          <span className="w-1 h-1 rounded-full bg-white/40 hidden sm:block" />
          <span>Assistenza dedicata</span>
        </motion.div>
      </Container>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <TrustStrip />
      <QuickQuote />
      <ServicesSection />
      <WhySection />
      <HowSection />
      <SplitFeature id="traslochi" overline="Traslochi premium" reverse
        title="Il trasloco, senza il peso del trasloco."
        text="Dallo smontaggio al rimontaggio, ci occupiamo di ogni fase con operatori specializzati e mezzi propri. Tu pensa alla nuova casa, al resto pensiamo noi."
        items={MOVING_ITEMS} img={IMAGES.traslochiRoom} cta="Organizza il tuo trasloco" ctaPreset={{ servizio: "trasloco" }} />
      <SplitFeature id="moto" overline="Spedizione moto"
        title="La tua moto viaggia con AXAL."
        text="Trasporto dedicato e assicurato per moto, scooter e veicoli a due ruote, con imballaggio professionale e fissaggio in totale sicurezza su tutto il territorio nazionale."
        img={IMAGES.moto} cta="Richiedi preventivo moto" ctaPreset={{ servizio: "moto" }} />
      <BusinessSection />
      <ReviewsSection />
      <GallerySection />
      <CoverageMap />
      <DarkCTA />
    </>
  );
}
